import { Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCar } from "react-icons/fa";
import { IoMdArrowBack } from "react-icons/io";
import { HiOutlineRectangleGroup } from "react-icons/hi2";

// Componentes UI
import Spinner from "../../../Spinner";
import EmptyData from "../../../../../components/EmptyData";
import { ToastComponent } from "../../../ToastComponent";
import { Filters } from "../../../../../components/Filters";
import LayoutToggle from "../../../../../components/LayoutToggle";
import ReportDropDown from "../../../DropDownButton/ReportDropDown";
import PositionsMap from "../../../Maps/PositionsMap";

// Subcomponentes locais
import { PositionsGrid } from "./components/PositionsGrid";
import { SearchDetails } from "../SearchDetails";
import {
  MaintenanceModal,
  CommandsModal,
  AvailableCommandsModal,
  ConfirmCommandModal,
} from "./modals/VehicleModals";

// Hooks
import { useVehicleFilters } from "./hooks/useVehicleFilters";
import { usePositionsData } from "./hooks/usePositionsData";
import { useVehicleActions } from "./hooks/useVehicleActions";
import { useGridColumns } from "./hooks/useGridColumns";

// Utils
import { calculateInitialPageSize } from "./utils/calculatePageSize";

import "@syncfusion/ej2-react-calendars/styles/tailwind.css";
import "../syncfusion-grid-overrides.css";

const PaginatedTableFleetPositions = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isLocalizationPage = location.pathname === "/localization";
  const isPositionsPage = props.activeTab === "positions";
  const useReportService =
    props.useReportService === true || isLocalizationPage || isPositionsPage;
  const showTopFilters = props.showTopFilters !== false;
  const showBackButton = props.showBackButton !== false;

  const { vehicleSelected: reduxVehicleSelected } = useSelector(
    (s) => s.vehicleSelectedReducer
  );
  const vehicleSelected = props.selectedVehicle ?? reduxVehicleSelected;
  const vehicleId = vehicleSelected?.clientId ?? vehicleSelected?.vehicleId;

  // Layout
  const [layoutMode, setLayoutMode] = useState("split");
  const [gridHeight, setGridHeight] = useState("100%");

  // Datas
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 1, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [startCommandsDate, setStartCommandsDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 365))
  );
  const [endCommandsDate, setEndCommandsDate] = useState(new Date());

  // Toast
  const [toastParams, setToastParams] = useState("");
  const handleToast = (params) => setToastParams(JSON.stringify(params));

  // Grid interaction
  const gridRef = useRef();
  const [selectedActionRow, setSelectedActionRow] = useState(null);
  const [handleClick, setHandleClick] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // --- Hooks ---
  const filters = useVehicleFilters({ vehicleSelected, showTopFilters });

  const positions = usePositionsData({
    vehicleId,
    selectedGroup: filters.selectedGroup,
    startDate,
    endDate,
    onlyEvents: props.onlyEvents,
    useReportService,
    activeTab: props.activeTab,
    pageSize: 10,
  });

  const actions = useVehicleActions({
    vehicleSelected,
    onToast: handleToast,
  });

  const { effectiveFields, getColumnProps } = useGridColumns({
    columns: props.columns ?? [],
    fields: props.fields ?? [],
    layoutMode,
    vehicleId: vehicleSelected?.vehicleId,
    onDateClick: (lat, lng) => {
      const index = positions.registersMap.findIndex(
        (item) => item.latitude === lat && item.longitude === lng
      );
      if (index !== -1) {
        setSelectedIndex(index);
        setHandleClick(true);
      }
    },
  });

  // Notifica o pai sobre mudanças de data
  useEffect(() => {
    if (props.onDatesChange) props.onDatesChange(startDate, endDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Reseta datas ao trocar de veículo
  useEffect(() => {
    if (props.activeTab === "positions" && vehicleSelected?.vehicleId) {
      const d = new Date();
      d.setHours(0, 0, 1, 0);
      setStartDate(d);
      setEndDate(new Date());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleSelected?.vehicleId]);

  // Pagesize adaptativo por resize
  const calculateMapAlignedGridHeight = () => {
    if (layoutMode === "grid-only") return "100%";

    const width = window.innerWidth;
    let mapHeightRatio = 0.68;
    if (width >= 2560) mapHeightRatio = 0.8;
    else if (width >= 1541) mapHeightRatio = 0.74;
    else if (width >= 1024) mapHeightRatio = 0.64;

    const mapHeight = window.innerHeight * mapHeightRatio - 50;

    return `${Math.max(220, mapHeight)}px`;
  };

  useEffect(() => {
    if (props.activeTab !== "positions") return;
    const handleResize = () => {
      const ps = calculateInitialPageSize();
      positions.setGridPageSettings((prev) => ({ ...prev, pageSize: ps, currentPage: 1 }));
      setGridHeight(calculateMapAlignedGridHeight());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeTab, layoutMode]);

  // --- Navegação ---
  const handleActiveTabGrid = () => {
    dispatch({ type: "title", value: t("TITLE.MY_FLEET") });
    props.chooseDisplay("gridAndMap", 0);
  };

  const handleSearchCommands = (start, end) => {
    actions.setShowCommandsModal(false);
    dispatch({ type: "activeTab", value: "commands" });
    props.chooseDisplay("commands", vehicleSelected.vehicleId);
    props.setCommandDates(start, end);
  };

  const handleActiveTabMapPositions = async () => {
    const list = await positions.fetchPositionsMap();
    await props.handleShowPositionMap(list);
  };

  const openVisitControl = (item) => {
    const lat = encodeURIComponent(item.latitude);
    const lng = encodeURIComponent(item.longitude);
    navigate("/visitpoint", { state: { lat, lng } });
  };

  const onContextMenuClick = (args) => {
    const row = selectedActionRow || gridRef.current?.getSelectedRecords?.()?.[0];
    if (!row) return;
    if (args.item.id === "editar-localizacao") openVisitControl(row);
  };

  const handleSearch = () => {
    // Reseta datas via hook interno ao executar busca
    positions.search();
    handleActiveTabMapPositions();
  };

  const clear = () => {
    const d = new Date();
    d.setHours(0, 0, 1, 0);
    setStartDate(d);
    setEndDate(new Date());
  };

  return (
    <>
      {positions.dataLoading ? (
        <Spinner />
      ) : (
        <>
          <ToastComponent
            params={toastParams}
            closeToast={() => setToastParams(JSON.stringify({ show: false }))}
          />

          {/* Cabeçalho: botão voltar + filtros de topo */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            {showBackButton ? (
              <div className="back-to-fleet">
                <button style={{ display: "flex", alignItems: "center" }} onClick={handleActiveTabGrid}>
                  <div className="icon-back-fleet"><IoMdArrowBack /></div>
                  <span>Voltar</span>
                </button>
              </div>
            ) : (
              <div />
            )}

            {showTopFilters && (
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <ReportDropDown
                  vehicleId={props.vehicleId}
                  selectedVehicle={props.vehicleFilter}
                  loading={positions.dataLoading}
                  startDate={startDate}
                  endDate={endDate}
                  groupId={filters.selectedGroup?.value}
                  onToast={setToastParams}
                />
                <Filters
                  id="veiculos"
                  isMulti={false}
                  title="Grupos"
                  hasSubTitle
                  subTitle="Todos os grupos"
                  options={filters.dataGroup}
                  icon={HiOutlineRectangleGroup}
                  hasIcon
                  pageTitle="Posições Detalhadas"
                  onManualSelection={() => filters.setIsManualSelection(true)}
                />
                <Filters
                  id="veiculosGroups"
                  isMulti={false}
                  title="Veículos"
                  hasSubTitle
                  subTitle="Todos os veículos"
                  options={filters.dataVehicle}
                  icon={FaCar}
                  hasIcon
                  pageTitle="Posições Detalhadas"
                  onManualSelection={() => filters.setIsManualSelection(true)}
                  isLoading={filters.filtersLoading}
                />
              </div>
            )}
          </div>

          {/* Corpo principal */}
          <div className="table-responsive" style={{ margin: 4 }}>
            {props.dataLoading ? (
              <Spinner />
            ) : (
              <div className="control-pane">
                <div className="control-section">
                  <SearchDetails
                    startDate={startDate}
                    setStartDate={setStartDate}
                    underMaintenace={actions.localUnderMaintenance}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    vehicleId={vehicleSelected.vehicleId}
                    vehicleFilter={props.vehicleFilter}
                    dataLoading={positions.dataLoading}
                    clear={clear}
                    search={handleSearch}
                    handleActiveMaintenace={() => actions.setShowMaintenanceModal(true)}
                    handleActiveCommands={() => actions.setShowCommandsModal(true)}
                    handleShowCommands={actions.handleShowCommands}
                    refresh={positions.refresh}
                    handleActiveTabGrid={handleActiveTabGrid}
                  />

                  <LayoutToggle layoutMode={layoutMode} onLayoutChange={setLayoutMode} />

                  {positions.gridPageSettings.totalRecordsCount === 0 ? (
                    <div style={{ margin: "20px 0" }}>
                      <EmptyData />
                    </div>
                  ) : (
                    <div
                      className="card-positions"
                      style={{ display: "flex", gap: 16, height: "100%", minHeight: "600px" }}
                    >
                      {layoutMode !== "map-only" && (
                        <PositionsGrid
                          gridRef={gridRef}
                          dataSource={positions.dataGridPositions}
                          pageSettings={positions.gridPageSettings}
                          effectiveFields={effectiveFields}
                          getColumnProps={getColumnProps}
                          layoutMode={layoutMode}
                          gridHeight={gridHeight}
                          onContextMenuClick={onContextMenuClick}
                          onActionMenuClick={(rowData, e) => {
                            setSelectedActionRow(rowData);
                            gridRef.current?.contextMenuModule?.contextMenu?.open(e.clientY, e.clientX);
                          }}
                        />
                      )}

                      {layoutMode !== "grid-only" && (
                        <Suspense fallback={<Spinner />}>
                          <PositionsMap
                            activeTab={props.activeTab}
                            mapLoading={props.mapLoading}
                            diaryPositions={positions.registersMap}
                            handleClick={handleClick}
                            selectedIndex={selectedIndex}
                            onHandled={() => setHandleClick(false)}
                            style={{
                              flex: layoutMode === "map-only" ? 1 : undefined,
                              width: layoutMode === "map-only" ? "100%" : "30%",
                            }}
                          />
                        </Suspense>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Modais */}
          <MaintenanceModal
            show={actions.showMaintenanceModal}
            onHide={() => actions.setShowMaintenanceModal(false)}
            localUnderMaintenance={actions.localUnderMaintenance}
            maintenanceMessage={actions.maintenanceMessage}
            startDate={actions.startMaintenanceDate}
            endDate={actions.endMaintenanceDate}
            onStartDateChange={actions.handleStartDateChange}
            onEndDateChange={actions.handleEndDateChange}
            onSubmit={() => actions.vehicleMaintenance(vehicleSelected.vehicleId)}
          />

          <CommandsModal
            show={actions.showCommandsModal}
            onHide={() => actions.setShowCommandsModal(false)}
            startDate={startCommandsDate}
            endDate={endCommandsDate}
            onStartDateChange={setStartCommandsDate}
            onEndDateChange={setEndCommandsDate}
            onSearch={handleSearchCommands}
          />

          <AvailableCommandsModal
            show={actions.showAvailableCommandsModal}
            onHide={() => actions.setShowAvailableCommandsModal(false)}
            commands={actions.availableCommands}
            onSendCommand={actions.handleOpenConfirmModal}
          />

          <ConfirmCommandModal
            show={actions.showConfirmModal}
            onHide={() => actions.setShowConfirmModal(false)}
            command={actions.commandToSend}
            vehicleName={actions.selectedVehicleName}
            onConfirm={actions.handleConfirmSend}
          />
        </>
      )}
    </>
  );
};

export default PaginatedTableFleetPositions;
